import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { PhpFpmSettingsPage } from "./PhpFpmSettingsPage";

type SiteRow = {
  id: string;
  domain: string;
  username?: string;
  type: string;
  phpVersion: string;
  status?: string;
  userId?: string;
};

type PhpVer = {
  version: string;
  available?: boolean;
  default?: boolean;
  binary?: string | null;
};

/**
 * Premium MultiPHP Manager — environment summary, bulk tools, enterprise table.
 */
export function MultiPhpManagerPage() {
  const [items, setItems] = useState<SiteRow[]>([]);
  const [versions, setVersions] = useState<PhpVer[]>([]);
  const [primaryIp, setPrimaryIp] = useState("");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [bulkVersion, setBulkVersion] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [search, setSearch] = useState("");
  const [phpFilter, setPhpFilter] = useState("all");
  const [menuId, setMenuId] = useState<string | null>(null);
  const [showBulk, setShowBulk] = useState(false);
  const [fpmTarget, setFpmTarget] = useState<{
    username: string;
    version: string;
  } | null>(null);
  const [showGlobalFpm, setShowGlobalFpm] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const [sitesRes, verRes, basicRes] = await Promise.all([
        api<{ items: SiteRow[] }>("/api/v1/sites"),
        api<{ items: PhpVer[] }>("/api/v1/sites/php-versions"),
        api<{ config?: { primaryIp?: string } }>(
          "/api/v1/server-config/basic"
        ).catch(() => ({ config: { primaryIp: "" } as { primaryIp?: string } }))
      ]);
      setItems(sitesRes.items || []);
      setVersions(verRes.items || []);
      setPrimaryIp(basicRes.config?.primaryIp || "");
      const def =
        (verRes.items || []).find((v) => v.default)?.version ||
        (verRes.items || []).find((v) => v.available)?.version ||
        "8.3";
      setBulkVersion((b) => b || def);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const defaultVersion =
    versions.find((v) => v.default)?.version ||
    versions.find((v) => v.available)?.version ||
    "8.3";

  const filtered = useMemo(() => {
    let list = [...items];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (s) =>
          s.domain.toLowerCase().includes(q) ||
          (s.username || "").toLowerCase().includes(q) ||
          s.phpVersion.toLowerCase().includes(q)
      );
    }
    if (phpFilter !== "all") {
      list = list.filter((s) => s.phpVersion === phpFilter);
    }
    list.sort((a, b) => a.domain.localeCompare(b.domain));
    return list;
  }, [items, search, phpFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (safePage - 1) * perPage,
    safePage * perPage
  );

  const selectedIds = Object.entries(selected)
    .filter(([, v]) => v)
    .map(([k]) => k);

  const versionCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of items) {
      m.set(s.phpVersion, (m.get(s.phpVersion) || 0) + 1);
    }
    return m;
  }, [items]);

  const customCount = items.filter(
    (s) => s.phpVersion !== defaultVersion
  ).length;

  const setOneVersion = async (site: SiteRow, phpVersion: string) => {
    if (phpVersion === site.phpVersion) return;
    setBusy(true);
    setError("");
    setMsg("");
    try {
      await api(`/api/v1/sites/${site.id}/php-version`, {
        method: "POST",
        body: JSON.stringify({ phpVersion })
      });
      setMsg(`${site.domain} → PHP ${phpVersion}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const applyBulk = async () => {
    if (!selectedIds.length || !bulkVersion) return;
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const res = await api<{
        updated: number;
        failed: { id: string; error: string }[];
      }>("/api/v1/sites/php-version/bulk", {
        method: "POST",
        body: JSON.stringify({
          siteIds: selectedIds,
          phpVersion: bulkVersion
        })
      });
      setMsg(
        `Set PHP ${bulkVersion} on ${res.updated} domain(s)` +
          (res.failed?.length ? ` · ${res.failed.length} failed` : "")
      );
      setSelected({});
      setShowBulk(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (showGlobalFpm) {
    return (
      <PhpFpmSettingsPage
        initialUsername=""
        initialVersion={defaultVersion}
        onBack={() => {
          setShowGlobalFpm(false);
          void load();
        }}
      />
    );
  }

  if (fpmTarget) {
    return (
      <PhpFpmSettingsPage
        initialUsername={fpmTarget.username}
        initialVersion={fpmTarget.version}
        onBack={() => {
          setFpmTarget(null);
          void load();
        }}
      />
    );
  }

  return (
    <div className="rp-page">
      <div className="rp-page-head">
        <div>
          <h1 className="rp-page-title">MultiPHP Manager</h1>
          <p className="rp-page-sub">
            Manage PHP versions and PHP-FPM settings for your domains.
          </p>
          <p className="rp-sum-hint" style={{ marginTop: 6 }}>
            PHP {defaultVersion} default · PHP-FPM enabled
          </p>
        </div>
        <div className="rp-page-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void load()}
            disabled={busy}
          >
            Refresh
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: "auto", marginTop: 0 }}
            onClick={() => setShowGlobalFpm(true)}
          >
            Global PHP-FPM
          </button>
        </div>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="rp-summary-strip">
        <div className="rp-sum">
          <span className="rp-sum-label">Domains</span>
          <span className="rp-sum-value">{items.length}</span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">PHP {defaultVersion}</span>
          <span className="rp-sum-value">
            {versionCounts.get(defaultVersion) || 0}
          </span>
          <span className="rp-sum-hint">On default</span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">Custom PHP</span>
          <span className="rp-sum-value">{customCount}</span>
          <span className="rp-sum-hint">Non-default version</span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">PHP-FPM</span>
          <span className="rp-sum-value" style={{ fontSize: 18 }}>
            Enabled
          </span>
        </div>
      </div>

      <div className="rp-toolbar">
        <input
          className="rp-toolbar-search"
          placeholder="Search domain or username…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          value={phpFilter}
          onChange={(e) => {
            setPhpFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All PHP versions</option>
          {versions.map((v) => (
            <option key={v.version} value={v.version}>
              PHP {v.version}
              {v.default ? " (default)" : ""}
            </option>
          ))}
        </select>
        <select
          value={perPage}
          onChange={(e) => {
            setPerPage(Number(e.target.value) || 50);
            setPage(1);
          }}
        >
          {[25, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n} / page
            </option>
          ))}
        </select>
        {selectedIds.length === 0 ? (
          <span className="rp-bulk-hint">
            Select domains for bulk PHP version change
          </span>
        ) : (
          <div className="rp-bulk-bar">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              style={{ width: "auto", marginTop: 0 }}
              onClick={() => setShowBulk(true)}
            >
              Change PHP Version ({selectedIds.length})
            </button>
          </div>
        )}
      </div>

      <div className="rp-panel">
        <table className="rp-acct-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input
                  type="checkbox"
                  checked={
                    pageItems.length > 0 &&
                    pageItems.every((s) => selected[s.id])
                  }
                  onChange={(e) => {
                    setSelected((prev) => {
                      const next = { ...prev };
                      for (const s of pageItems) next[s.id] = e.target.checked;
                      return next;
                    });
                  }}
                  aria-label="Select page"
                />
              </th>
              <th>Domain</th>
              <th>Account</th>
              <th>IP</th>
              <th>PHP</th>
              <th>FPM</th>
              <th>Configuration</th>
              <th style={{ width: 100 }} />
            </tr>
          </thead>
          <tbody>
            {pageItems.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: 36 }}>
                  <span className="faint">No domains match your filters.</span>
                </td>
              </tr>
            ) : (
              pageItems.map((s) => {
                const isDefault = s.phpVersion === defaultVersion;
                return (
                  <tr key={s.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={Boolean(selected[s.id])}
                        onChange={(e) =>
                          setSelected((p) => ({
                            ...p,
                            [s.id]: e.target.checked
                          }))
                        }
                      />
                    </td>
                    <td>
                      <strong style={{ fontWeight: 650 }}>{s.domain}</strong>
                      <div className="faint" style={{ fontSize: 12 }}>
                        {s.type}
                      </div>
                    </td>
                    <td className="mono">{s.username || "—"}</td>
                    <td className="mono faint">{primaryIp || "—"}</td>
                    <td>
                      <span className="mono" style={{ fontWeight: 650 }}>
                        PHP {s.phpVersion}
                      </span>
                    </td>
                    <td>
                      <span className="rp-status rp-status-ok">
                        <span className="rp-status-dot" />
                        Enabled
                      </span>
                    </td>
                    <td>
                      <span
                        className={`rp-status ${
                          isDefault ? "rp-status-muted" : "rp-status-info"
                        }`}
                      >
                        {isDefault ? "Default" : "Custom"}
                      </span>
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          justifyContent: "flex-end",
                          alignItems: "center"
                        }}
                      >
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={busy || !s.username}
                          onClick={() =>
                            setFpmTarget({
                              username: s.username || "",
                              version: s.phpVersion || defaultVersion
                            })
                          }
                        >
                          PHP-FPM
                        </button>
                        <div className="rp-menu-wrap">
                          <button
                            type="button"
                            className="rp-menu-btn"
                            onClick={() =>
                              setMenuId((id) => (id === s.id ? null : s.id))
                            }
                          >
                            •••
                          </button>
                          {menuId === s.id ? (
                            <div className="rp-menu">
                              <div
                                style={{
                                  padding: "6px 10px",
                                  fontSize: 11,
                                  fontWeight: 650,
                                  color: "var(--faint)"
                                }}
                              >
                                Change PHP
                              </div>
                              {versions
                                .filter((v) => v.available !== false)
                                .map((v) => (
                                  <button
                                    key={v.version}
                                    type="button"
                                    onClick={() => {
                                      setMenuId(null);
                                      void setOneVersion(s, v.version);
                                    }}
                                  >
                                    PHP {v.version}
                                    {v.default ? " (default)" : ""}
                                  </button>
                                ))}
                              <button
                                type="button"
                                onClick={() => {
                                  setMenuId(null);
                                  if (s.username)
                                    setFpmTarget({
                                      username: s.username,
                                      version: s.phpVersion
                                    });
                                }}
                              >
                                PHP-FPM Settings
                              </button>
                              {!isDefault ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMenuId(null);
                                    void setOneVersion(s, defaultVersion);
                                  }}
                                >
                                  Reset to Global PHP
                                </button>
                              ) : null}
                              <button
                                type="button"
                                onClick={() => {
                                  setMenuId(null);
                                  window.open(
                                    `http://${s.domain}`,
                                    "_blank",
                                    "noopener"
                                  );
                                }}
                              >
                                View Domain
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {totalPages > 1 ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 14px",
              borderTop: "1px solid var(--line)",
              fontSize: 13,
              color: "var(--muted)"
            }}
          >
            <span>
              Page {safePage} of {totalPages} · {filtered.length} domains
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={safePage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {showBulk ? (
        <div className="rp-drawer-bg" onClick={() => setShowBulk(false)}>
          <div
            className="rp-drawer"
            style={{ width: "min(420px, 100%)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rp-drawer-hd">
              <div>
                <h2>Change PHP Version</h2>
                <p>{selectedIds.length} domain(s) selected</p>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setShowBulk(false)}
              >
                ✕
              </button>
            </div>
            <div className="rp-drawer-body">
              <div className="field">
                <label>New PHP version</label>
                <select
                  value={bulkVersion}
                  onChange={(e) => setBulkVersion(e.target.value)}
                >
                  {versions.map((v) => (
                    <option key={v.version} value={v.version}>
                      PHP {v.version}
                      {v.default ? " (default)" : ""}
                      {v.available === false ? " — n/a" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <p className="card-meta" style={{ marginTop: 12 }}>
                PHP-FPM pool settings are kept. Use Global PHP-FPM or per-user
                settings to change process limits.
              </p>
            </div>
            <div className="rp-drawer-ft">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowBulk(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: "auto", marginTop: 0 }}
                disabled={busy || !bulkVersion}
                onClick={() => void applyBulk()}
              >
                {busy ? "Applying…" : "Apply Changes"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
